import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { useGetBackofficeOrganizationCreditsQuery } from "modules/backoffice/hooks/organizations/useGetBackofficeOrganizationCreditsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeGetCompanyCreditsResponseDataSchema } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof BackofficeGetCompanyCreditsResponseDataSchema>["packages"][number]
>()

type CreditsQueryReturn = ReturnType<typeof useGetBackofficeOrganizationCreditsQuery>

type Props = {
  credits: CreditsQueryReturn["data"]
  isPending: CreditsQueryReturn["isPending"]
}

export const BackofficeCreditsTable = ({ credits, isPending }: Props) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("createdAt", {
          header: t("backoffice.credits.table.addedAt"),
          cell: (createdAt) => <DateDisplay date={createdAt.getValue()} />,
        }),
        columnHelper.accessor("employeeName", {
          header: t("backoffice.credits.table.addedBy"),
        }),
        columnHelper.accessor("credits", {
          header: t("backoffice.credits.table.credits"),
          meta: { align: "right" },
        }),
        columnHelper.accessor("remaining", {
          header: t("backoffice.credits.table.remaining"),
          meta: { align: "right" },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: credits?.packages ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || credits === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if ((credits.packages ?? []).length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.credits.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={`${row.id}`}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
